import { useState, useMemo, useCallback } from "react";
import { useSearchParams } from "react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  type SortingState,
  type ColumnDef,
  flexRender,
} from "@tanstack/react-table";
import {
  Plus,
  Search,
  ChefHat,
  Pencil,
  Table2,
  Columns3,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { toast } from "sonner";

import { useApiHeaders } from "@/hooks/use-api-headers";
import { useTranslation } from "@/i18n/use-translation";
import type { TFunction } from "@/i18n/catalog";
import { formatDate } from "@/lib/format";
import { useAdminOrgStore } from "@/stores/admin-org";
import { ConfigOverrides } from "@/components/config-overrides";
import { ExportButton } from "@/components/export-button";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { KanbanBoard } from "@/components/kanban-board";
import {
  configOptions,
  KITCHEN_TYPE_OPTIONS,
  KITCHEN_TYPE_VALUES,
  optionLabel,
} from "@/lib/config-options";

const PAGE_SIZE = 10;

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:8000";

interface Kitchen {
  id: string;
  site_id: string;
  name: string;
  slug: string;
  kitchen_type: string;
  status: string;
  capacity: number | null;
  currency: string | null;
  timezone: string | null;
  default_weight_unit: string | null;
  waste_pct_target: number | null;
  waste_per_cover_target_g: number | null;
  created_at: string;
}

interface Site {
  id: string;
  name: string;
  account_id: string;
}

interface Account {
  id: string;
  name: string;
}

/** Built inside the component and memoised on `t`, so the messages are the reader's — a
 * module-level schema would freeze whichever locale happened to be active at import
 * (POS2-1133 pinned decision, the `makeSchema(t)` factory CP3a landed). */
function makeCreateKitchenSchema(t: TFunction) {
  return z.object({
    site_id: z.string().min(1, t("validation.siteRequired")),
    name: z.string().min(1, t("validation.nameRequired")),
    kitchen_type: z.enum(KITCHEN_TYPE_VALUES).default("main_kitchen"),
    capacity: z.coerce.number().int().positive().optional(),
  });
}

type CreateKitchenForm = z.infer<ReturnType<typeof makeCreateKitchenSchema>>;

const STATUS_STYLES: Record<string, string> = {
  active: "border-transparent bg-emerald-50 text-emerald-700",
  inactive: "border-transparent bg-gray-50 text-gray-400",
  seasonal: "border-transparent bg-blue-50 text-blue-700",
};

const STATUSES = ["active", "inactive", "seasonal"] as const;

export default function SettingsKitchensPage() {
  const { locale, t } = useTranslation();
  const headers = useApiHeaders();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<Kitchen | null>(null);
  const [view, setView] = useState<"table" | "kanban">("table");
  const [sorting, setSorting] = useState<SortingState>([]);

  const contextSiteId = useAdminOrgStore((s) => s.siteId);
  const siteFilter = contextSiteId || searchParams.get("site");

  const kitchensQuery = useQuery<Kitchen[]>({
    queryKey: ["kitchens", siteFilter],
    queryFn: async () => {
      const url = siteFilter
        ? `${API_URL}/api/v1/kitchens?site_id=${siteFilter}`
        : `${API_URL}/api/v1/kitchens`;
      const res = await fetch(url, { headers });
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(`${res.status}: ${text || res.statusText}`);
      }
      return (await res.json()).data;
    },
    enabled: !!headers.Authorization.replace("Bearer ", ""),
  });

  const sitesQuery = useQuery<Site[]>({
    queryKey: ["sites"],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/v1/sites`, { headers });
      if (!res.ok) return [];
      return (await res.json()).data;
    },
    enabled: !!headers.Authorization.replace("Bearer ", ""),
  });

  const accountsQuery = useQuery<Account[]>({
    queryKey: ["accounts"],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/v1/accounts`, { headers });
      if (!res.ok) return [];
      return (await res.json()).data;
    },
    enabled: !!headers.Authorization.replace("Bearer ", ""),
  });

  const sites = useMemo(() => sitesQuery.data ?? [], [sitesQuery.data]);
  const accounts = useMemo(() => accountsQuery.data ?? [], [accountsQuery.data]);

  const kitchenDisplayName = useCallback(
    (kitchen: Kitchen) => {
      const site = sites.find((s) => s.id === kitchen.site_id);
      const acc = site ? accounts.find((a) => a.id === site.account_id) : null;
      const prefix = acc && site ? `${acc.name}: ${site.name}` : (site?.name ?? "");
      return prefix ? `${prefix} - ${kitchen.name}` : kitchen.name;
    },
    [sites, accounts],
  );

  const filtered = useMemo(() => {
    if (!kitchensQuery.data) return [];
    if (!search.trim()) return kitchensQuery.data;
    const q = search.toLowerCase();
    return kitchensQuery.data.filter((k) => kitchenDisplayName(k).toLowerCase().includes(q));
  }, [kitchensQuery.data, search, kitchenDisplayName]);

  const createMutation = useMutation({
    mutationFn: async (data: CreateKitchenForm) => {
      const res = await fetch(`${API_URL}/api/v1/kitchens`, {
        method: "POST",
        headers,
        body: JSON.stringify({ ...data, capacity: data.capacity ?? null }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error?.message ?? "Failed to create kitchen");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["kitchens"] });
      toast.success(t("settings.kitchenCreated"));
      setOpen(false);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const editMutation = useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: {
        name: string;
        kitchen_type: string;
        capacity?: number;
        currency?: string;
        timezone?: string;
        default_weight_unit?: string;
        waste_pct_target?: string;
        waste_per_cover_target_g?: string;
      };
    }) => {
      const res = await fetch(`${API_URL}/api/v1/kitchens/${id}`, {
        method: "PATCH",
        headers,
        body: JSON.stringify({
          name: data.name,
          kitchen_type: data.kitchen_type,
          capacity: data.capacity ?? null,
          currency: data.currency || null,
          timezone: data.timezone || null,
          default_weight_unit: data.default_weight_unit || null,
          waste_pct_target: data.waste_pct_target ? Number(data.waste_pct_target) : null,
          waste_per_cover_target_g: data.waste_per_cover_target_g
            ? Number(data.waste_per_cover_target_g)
            : null,
        }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error?.message ?? "Failed to update kitchen");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["kitchens"] });
      toast.success(t("settings.kitchenUpdated"));
      setEditing(null);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const statusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      const res = await fetch(`${API_URL}/api/v1/kitchens/${id}`, {
        method: "PATCH",
        headers,
        body: JSON.stringify({ status }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error?.message ?? "Failed to update status");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["kitchens"] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm<CreateKitchenForm>({
    resolver: zodResolver(useMemo(() => makeCreateKitchenSchema(t), [t])),
    defaultValues: { kitchen_type: "main_kitchen", site_id: siteFilter ?? "" },
  });

  const currentSite = siteFilter ? sitesQuery.data?.find((s) => s.id === siteFilter) : null;

  const columns = useMemo<ColumnDef<Kitchen>[]>(
    () => [
      {
        id: "displayName",
        accessorFn: (row) => kitchenDisplayName(row),
        header: ({ column }) => (
          <span
            role="button"
            tabIndex={0}
            className="inline-flex cursor-pointer select-none items-center gap-1"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ")
                column.toggleSorting(column.getIsSorted() === "asc");
            }}
          >
            {t("settings.name")}
            <ArrowUpDown className="size-3 text-muted-foreground/50" />
          </span>
        ),
        cell: ({ row }) => (
          <span className="text-sm font-medium">{kitchenDisplayName(row.original)}</span>
        ),
      },
      {
        accessorKey: "kitchen_type",
        header: "Type",
        cell: ({ row }) => (
          <Badge variant="secondary">
            {optionLabel(t, KITCHEN_TYPE_OPTIONS, row.original.kitchen_type)}
          </Badge>
        ),
      },
      {
        accessorKey: "capacity",
        header: "Capacity",
        cell: ({ row }) => (
          <span className="text-sm text-muted-foreground">{row.original.capacity ?? "—"}</span>
        ),
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: ({ row }) => (
          <Badge
            className={
              STATUS_STYLES[row.original.status] ?? "border-transparent bg-gray-100 text-gray-600"
            }
          >
            {row.original.status}
          </Badge>
        ),
      },
      {
        accessorKey: "created_at",
        header: "Created",
        cell: ({ row }) => (
          <span className="text-xs text-muted-foreground">
            {formatDate(locale, row.original.created_at)}
          </span>
        ),
      },
      {
        id: "actions",
        cell: ({ row }) => (
          <Button
            size="icon"
            variant="ghost"
            className="size-7"
            onClick={() => setEditing(row.original)}
          >
            <Pencil className="size-3.5" />
          </Button>
        ),
      },
    ],
    [kitchenDisplayName, locale, t],
  );

  const table = useReactTable({
    data: filtered,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: { pagination: { pageSize: PAGE_SIZE } },
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-medium">
            {currentSite
              ? t("settings.kitchensTitleForSite", { site: currentSite.name })
              : t("settings.kitchensTitle")}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {currentSite
              ? t("settings.kitchensDescriptionForSite")
              : t("settings.kitchensDescriptionAll")}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <ExportButton entity="kitchens" />
          <Dialog
            open={open}
            onOpenChange={(v) => {
              setOpen(v);
              if (!v) reset({ kitchen_type: "main_kitchen", site_id: siteFilter ?? "" });
            }}
          >
            <DialogTrigger asChild>
              <Button size="sm">
                <Plus className="mr-1 size-4" />
                {t("settings.newKitchen")}
              </Button>
            </DialogTrigger>
            <DialogContent className="flex max-h-[85vh] flex-col overflow-hidden">
              <DialogHeader>
                <DialogTitle>{t("settings.createKitchen")}</DialogTitle>
              </DialogHeader>
              <form
                onSubmit={handleSubmit((d) => createMutation.mutate(d))}
                className="flex min-h-0 flex-1 flex-col gap-4"
              >
                <div className="-mx-6 min-h-0 flex-1 space-y-4 overflow-y-auto px-6">
                  <div>
                    <label className="text-sm font-medium" htmlFor="site_id">
                      {t("settings.site")}
                    </label>
                    <select
                      id="site_id"
                      className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm"
                      {...register("site_id")}
                    >
                      <option value="">{t("settings.selectSite")}</option>
                      {sitesQuery.data?.map((site) => (
                        <option key={site.id} value={site.id}>
                          {site.name}
                        </option>
                      ))}
                    </select>
                    {errors.site_id && (
                      <p className="mt-1 text-xs text-destructive">{errors.site_id.message}</p>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-sm font-medium" htmlFor="name">
                        {t("settings.kitchenName")}
                      </label>
                      <Input
                        id="name"
                        placeholder={t("settings.kitchenNamePlaceholder")}
                        {...register("name")}
                      />
                      {errors.name && (
                        <p className="mt-1 text-xs text-destructive">{errors.name.message}</p>
                      )}
                    </div>
                    <div>
                      <label className="text-sm font-medium" htmlFor="kitchen_type">
                        {t("settings.type")}
                      </label>
                      <select
                        id="kitchen_type"
                        className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm"
                        {...register("kitchen_type")}
                      >
                        {configOptions(t, KITCHEN_TYPE_OPTIONS).map(({ value, label }) => (
                          <option key={value} value={value}>
                            {label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium" htmlFor="capacity">
                      {t("settings.capacityCovers")}
                    </label>
                    <Input
                      id="capacity"
                      type="number"
                      placeholder={t("settings.capacityPlaceholder")}
                      {...register("capacity")}
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  className="w-full"
                  disabled={!isValid || createMutation.isPending}
                >
                  {createMutation.isPending ? t("settings.creating") : t("settings.createKitchen")}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {kitchensQuery.data && kitchensQuery.data.length > 0 && (
        <div className="flex items-center gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder={t("settings.searchKitchens")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <div className="flex items-center rounded-md border border-gray-200">
            <Button
              size="sm"
              variant={view === "table" ? "secondary" : "ghost"}
              className="h-8 rounded-r-none px-2.5"
              onClick={() => setView("table")}
            >
              <Table2 className="size-4" />
            </Button>
            <Button
              size="sm"
              variant={view === "kanban" ? "secondary" : "ghost"}
              className="h-8 rounded-l-none px-2.5"
              onClick={() => setView("kanban")}
            >
              <Columns3 className="size-4" />
            </Button>
          </div>
        </div>
      )}

      {kitchensQuery.isLoading && (
        <div className="space-y-2">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-10 w-full rounded" />
          ))}
        </div>
      )}

      {kitchensQuery.isError && (
        <div className="rounded border border-destructive/50 bg-destructive/10 p-3">
          <p className="text-sm font-medium text-destructive">{t("settings.kitchensLoadFailed")}</p>
          <p className="mt-1 font-mono text-xs text-destructive/80">
            {kitchensQuery.error instanceof Error
              ? kitchensQuery.error.message
              : t("settings.unknownError")}
          </p>
        </div>
      )}

      {kitchensQuery.data && kitchensQuery.data.length === 0 && (
        <div className="flex flex-col items-center gap-2 py-12 text-center text-muted-foreground">
          <ChefHat className="size-10 text-gray-300" />
          <p className="text-sm">{t("settings.noKitchens")}</p>
          <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
            {t("settings.createFirstKitchen")}
          </Button>
        </div>
      )}

      {filtered.length > 0 && view === "table" && (
        <>
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((hg) => (
                <TableRow key={hg.id}>
                  {hg.headers.map((header) => (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows.map((row) => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {table.getPageCount() > 1 && (
            <div className="flex items-center justify-between pt-4">
              <p className="text-xs text-muted-foreground">
                {t("settings.pageOf", {
                  page: table.getState().pagination.pageIndex + 1,
                  total: table.getPageCount(),
                })}
              </p>
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="icon"
                  className="size-8"
                  onClick={() => table.previousPage()}
                  disabled={!table.getCanPreviousPage()}
                >
                  <ChevronLeft className="size-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  className="size-8"
                  onClick={() => table.nextPage()}
                  disabled={!table.getCanNextPage()}
                >
                  <ChevronRight className="size-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {filtered.length > 0 && view === "kanban" && (
        <KanbanBoard
          statuses={STATUSES}
          statusStyles={STATUS_STYLES}
          items={filtered}
          getStatus={(k) => k.status}
          getId={(k) => k.id}
          onStatusChange={(id, status) => statusMutation.mutate({ id, status })}
          renderCard={(kitchen) => (
            <>
              <p className="text-sm font-medium">{kitchenDisplayName(kitchen)}</p>
              <div className="mt-1 flex items-center justify-between">
                <Badge variant="secondary" className="text-[10px]">
                  {optionLabel(t, KITCHEN_TYPE_OPTIONS, kitchen.kitchen_type)}
                </Badge>
                <span className="text-xs text-muted-foreground">
                  {kitchen.capacity ? `${kitchen.capacity} covers` : ""}
                </span>
              </div>
              <div className="mt-2 flex items-center justify-end">
                <Button
                  size="icon"
                  variant="ghost"
                  className="size-6"
                  onClick={() => setEditing(kitchen)}
                >
                  <Pencil className="size-3" />
                </Button>
              </div>
            </>
          )}
        />
      )}

      <EditKitchenDialog
        kitchen={editing}
        onClose={() => setEditing(null)}
        onSave={(data) => editing && editMutation.mutate({ id: editing.id, data })}
        isPending={editMutation.isPending}
      />

      {search && filtered.length === 0 && kitchensQuery.data && kitchensQuery.data.length > 0 && (
        <div className="flex flex-col items-center gap-2 py-12 text-center text-muted-foreground">
          <p className="text-sm">{t("settings.noResultsFor", { search })}</p>
          <Button size="sm" variant="outline" onClick={() => setSearch("")}>
            {t("settings.clearSearch")}
          </Button>
        </div>
      )}
    </div>
  );
}

function EditKitchenDialog({
  kitchen,
  onClose,
  onSave,
  isPending,
}: {
  kitchen: Kitchen | null;
  onClose: () => void;
  onSave: (data: {
    name: string;
    kitchen_type: string;
    capacity?: number;
    currency?: string;
    timezone?: string;
    default_weight_unit?: string;
    waste_pct_target?: string;
    waste_per_cover_target_g?: string;
  }) => void;
  isPending: boolean;
}) {
  const { t } = useTranslation();
  const editSchema = z.object({
    name: z.string().min(1, t("validation.nameRequired")),
    kitchen_type: z.enum(KITCHEN_TYPE_VALUES),
    capacity: z.coerce.number().int().positive().optional(),
    currency: z.string().optional(),
    timezone: z.string().optional(),
    default_weight_unit: z.string().optional(),
    waste_pct_target: z
      .string()
      .optional()
      .refine((v) => !v || (Number(v) >= 0 && Number(v) <= 100), "Must be between 0 and 100"),
    waste_per_cover_target_g: z
      .string()
      .optional()
      .refine((v) => !v || (Number.isInteger(Number(v)) && Number(v) > 0), "Whole grams above 0"),
  });
  type EditForm = z.infer<typeof editSchema>;

  const form = useForm<EditForm>({ resolver: zodResolver(editSchema) });

  const [prevId, setPrevId] = useState<string | null>(null);
  if (kitchen && kitchen.id !== prevId) {
    setPrevId(kitchen.id);
    form.reset({
      name: kitchen.name,
      kitchen_type: kitchen.kitchen_type as EditForm["kitchen_type"],
      capacity: kitchen.capacity ?? undefined,
      currency: kitchen.currency ?? "",
      timezone: kitchen.timezone ?? "",
      default_weight_unit: kitchen.default_weight_unit ?? "",
      waste_pct_target: kitchen.waste_pct_target != null ? String(kitchen.waste_pct_target) : "",
      waste_per_cover_target_g:
        kitchen.waste_per_cover_target_g != null ? String(kitchen.waste_per_cover_target_g) : "",
    });
  }

  return (
    <Dialog open={!!kitchen} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="flex max-h-[85vh] flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle>{t("settings.editKitchen")}</DialogTitle>
        </DialogHeader>
        <form onSubmit={form.handleSubmit(onSave)} className="flex min-h-0 flex-1 flex-col gap-4">
          <div className="-mx-6 min-h-0 flex-1 space-y-4 overflow-y-auto px-6">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium" htmlFor="edit-kitchen-name">
                  {t("settings.kitchenName")}
                </label>
                <Input id="edit-kitchen-name" {...form.register("name")} />
                {form.formState.errors.name && (
                  <p className="mt-1 text-xs text-destructive">
                    {form.formState.errors.name.message}
                  </p>
                )}
              </div>
              <div>
                <label className="text-sm font-medium" htmlFor="edit-kitchen-type">
                  {t("settings.type")}
                </label>
                <select
                  id="edit-kitchen-type"
                  className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm"
                  {...form.register("kitchen_type")}
                >
                  {configOptions(t, KITCHEN_TYPE_OPTIONS).map(({ value, label }) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium" htmlFor="edit-kitchen-capacity">
                {t("settings.capacityCovers")}
              </label>
              <Input id="edit-kitchen-capacity" type="number" {...form.register("capacity")} />
            </div>
            <ConfigOverrides
              entityType="kitchen"
              entityId={kitchen?.id ?? null}
              currentCurrency={form.watch("currency")}
              currentTimezone={form.watch("timezone")}
              currentWeightUnit={form.watch("default_weight_unit")}
              currentWastePctTarget={form.watch("waste_pct_target")}
              currentWastePerCoverTarget={form.watch("waste_per_cover_target_g")}
              onCurrencyChange={(v) => form.setValue("currency", v)}
              onTimezoneChange={(v) => form.setValue("timezone", v)}
              onWeightUnitChange={(v) => form.setValue("default_weight_unit", v)}
              onWastePctTargetChange={(v) =>
                form.setValue("waste_pct_target", v, { shouldValidate: true })
              }
              onWastePerCoverTargetChange={(v) =>
                form.setValue("waste_per_cover_target_g", v, { shouldValidate: true })
              }
              wastePctTargetError={form.formState.errors.waste_pct_target?.message}
              wastePerCoverTargetError={form.formState.errors.waste_per_cover_target_g?.message}
            />
          </div>
          <Button type="submit" className="w-full" disabled={isPending}>
            {isPending ? t("common.saving") : t("settings.saveChangesButton")}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
